import React, { useRef, useEffect } from 'react';

import useEvent from 'use-event-callback';
import { Link } from 'gatsby';
import { useMenuStore, useStoreState } from '@ariakit/react';
import * as ActionList from '@periodica/ui-kit/actionList';
import * as Tooltip from '@periodica/ui-kit/tooltip';
import { Gift, SmallArrowDown } from '@periodica/ui-kit/icons';
import type { StrapiHeaderNavItem } from '@customTypes/strapiData';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { isUrlExternal } from '@utils/isUrlExternal';

import { Button } from '@periodica/ui-kit';
import gtm from '@periodica/analytics/gtm';
import * as styles from './index.module.css';

type MenuArgs = {
  items: StrapiHeaderNavItem[];
  useIntersectionObserver?: boolean;
};

export function Menu({ items, useIntersectionObserver = false }: MenuArgs) {
  return (
    <>
      {items.map((item: StrapiHeaderNavItem) =>
        item.url ? (
          <HeaderLinkItem item={item} key={item.rank} />
        ) : (
          <HeaderButtonItem
            key={item.rank}
            item={item}
            useIntersectionObserver={useIntersectionObserver}
          />
        )
      )}
      <ReferralMenuLink />
    </>
  );
}

function HeaderLinkItem({ item }: { item: StrapiHeaderNavItem }) {
  const icon = item.icon ? (
    <img className={styles.img} src={item.icon?.localFile?.publicURL} alt="Иконка" />
  ) : null;
  if (item.externalUrl || isUrlExternal(item.url as string)) {
    return (
      <a className={styles.item} href={item.url} target="_blank" rel="noreferrer">
        {icon}
        <span>{item.title}</span>
      </a>
    );
  }

  return (
    <Link className={styles.item} to={item.url as string}>
      {icon}
      <span>{item.title}</span>
    </Link>
  );
}

function HeaderButtonItem({
  item,
  useIntersectionObserver,
}: {
  item: StrapiHeaderNavItem;
  useIntersectionObserver: boolean;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menu = useMenuStore();
  const isOpen = useStoreState(menu, 'open');

  const intersectionCallback = useEvent((entries: Array<IntersectionObserverEntry>) => {
    const [entry] = entries;

    if (!entry.isIntersecting && isOpen) {
      menu.hide();
    }
  });

  const isMobile = useMediaQuery('(max-width: 1024px)', true);

  useEffect(() => {
    const refElement = buttonRef.current;
    if (useIntersectionObserver && refElement) {
      const observer = new IntersectionObserver(intersectionCallback, {
        rootMargin: '0px',
        threshold: 0.5,
      });
      observer.observe(refElement);
      return () => {
        observer.unobserve(refElement);
      };
    }
  }, [buttonRef, intersectionCallback, useIntersectionObserver]);

  const showMenu = () => {
    menu.show();
  };

  const hideMenu = () => {
    if (!isMobile) {
      menu.hide();
    }
  };

  return (
    <ActionList.Provider>
      <ActionList.Trigger
        store={menu}
        onFocusVisible={showMenu}
        toggleOnClick={
          !isMobile
            ? () => {
                showMenu();
                return false;
              }
            : undefined
        }
        showOnHover={!isMobile}
        onMouseLeave={hideMenu}
        render={<button type="button" className={styles.item} ref={buttonRef} />}
      >
        {item.icon && (
          <img className={styles.img} src={item.icon?.localFile?.publicURL} alt="Иконка" />
        )}
        <span>{item.title}</span>
        <SmallArrowDown size={16} />
      </ActionList.Trigger>
      <ActionList.ActionList store={menu} gutter={1} onMouseLeave={hideMenu}>
        {item.items.length &&
          item.items.map((item) =>
            item.externalUrl || isUrlExternal(item.url) ? (
              <ActionList.ExternalLink key={item.url} href={item.url.trim()} tabbable>
                {item.title}
              </ActionList.ExternalLink>
            ) : (
              <ActionList.Item key={item.url} render={<Link to={item.url.trim()} />} tabbable>
                {item.title}
              </ActionList.Item>
            )
          )}
      </ActionList.ActionList>
    </ActionList.Provider>
  );
}

function ReferralMenuLink() {
  const handleFollowReferralLink = () => {
    gtm.logEvent({ name: 'e_site_header_follow_referral_link' });
  };
  return (
    <Tooltip.Provider placement="bottom">
      <Tooltip.Anchor>
        <Button variant="brandSecondary" size="medium" asChild className={styles.referralButton}>
          <a
            href={`${process.env.GATSBY_CONSTRUCTOR_URL}/referral`}
            target="_blank"
            rel="noreferrer"
            onClick={handleFollowReferralLink}
          >
            <Gift />
          </a>
        </Button>
      </Tooltip.Anchor>
      <Tooltip.Tooltip>
        Пригласите друзей
        <br />и получите подарки!
      </Tooltip.Tooltip>
    </Tooltip.Provider>
  );
}
